import React from "react";
import { X, Flag, Pencil, Trash2, Plus, Calendar, Tag } from "lucide-react";
import type { ChartHitoItem } from "./common/ChartTooltip";

interface ManageHitosModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientSlug: string;
  hitos: any[];
  canManageHitos: boolean;
  onEditHito: (hito: ChartHitoItem) => void;
  onDeleteHito: (hito: ChartHitoItem) => void;
  onOpenCreateModal: () => void;
}

export const ManageHitosModal: React.FC<ManageHitosModalProps> = ({
  isOpen,
  onClose,
  clientSlug,
  hitos,
  canManageHitos,
  onEditHito,
  onDeleteHito,
  onOpenCreateModal,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="detail-modal-backdrop"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "16px",
      }}
    >
      <div
        className="detail-modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--surface, #151923)",
          border: "1px solid var(--line, rgba(255, 255, 255, 0.1))",
          borderRadius: "14px",
          width: "100%",
          maxWidth: "640px",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.5)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid var(--line, rgba(255, 255, 255, 0.08))",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "8px",
                background: "rgba(239, 68, 68, 0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ef4444",
              }}
            >
              <Flag size={16} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: "var(--text, #f6f7fb)" }}>
                Hitos y acciones registradas
              </h3>
              <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: "var(--muted, #8e96a8)" }}>
                Cliente: <span style={{ color: "#7d96ff", fontWeight: 600 }}>{clientSlug}</span> ({hitos.length} {hitos.length === 1 ? "hito" : "hitos"})
              </p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {canManageHitos && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenCreateModal();
                }}
                className="chart-hito-btn"
                style={{ padding: "6px 10px", fontSize: "11.5px" }}
              >
                <Plus size={13} />
                <span>Nuevo hito</span>
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--muted, #8e96a8)",
                cursor: "pointer",
                padding: "6px",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* List */}
        <div style={{ padding: "16px 20px", overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: "10px" }}>
          {hitos.length === 0 ? (
            <div style={{ textAlign: "center", padding: "36px 12px", color: "var(--muted, #8e96a8)", fontSize: "13px" }}>
              No hay hitos registrados para este cliente.
            </div>
          ) : (
            hitos.map((h: any, idx: number) => {
              const hitoItem: ChartHitoItem = {
                id: String(h.id_item || h.id || ""),
                title: String(h.titulo || "Sin título"),
                date: String(h.executed_at || h.date || "").slice(0, 10),
                comment: String(h.comentario || ""),
                category: String(h.dominio || h.categoria || "General"),
              };
              return (
                <div
                  key={hitoItem.id || idx}
                  style={{
                    padding: "12px 14px",
                    background: "rgba(255, 255, 255, 0.02)",
                    border: "1px solid var(--line, rgba(255, 255, 255, 0.06))",
                    borderRadius: "10px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "12px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                      <span style={{ fontSize: "13.5px", fontWeight: 700, color: "var(--text, #f6f7fb)" }}>
                        {hitoItem.title}
                      </span>
                      <span
                        style={{
                          fontSize: "10.5px",
                          padding: "1px 6px",
                          borderRadius: "4px",
                          background: "rgba(56, 189, 248, 0.15)",
                          color: "#38bdf8",
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        {hitoItem.category}
                      </span>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "12px", fontSize: "11.5px", color: "var(--muted, #8e96a8)", marginBottom: hitoItem.comment ? "6px" : "0" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                        <Calendar size={12} />
                        {hitoItem.date || "Sin fecha"}
                      </span>
                    </div>

                    {hitoItem.comment && (
                      <p style={{ margin: 0, fontSize: "12px", color: "var(--text, #d0d6e2)", lineHeight: 1.4 }}>
                        {hitoItem.comment}
                      </p>
                    )}
                  </div>

                  {canManageHitos && (
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <button
                        type="button"
                        onClick={() => {
                          onClose();
                          onEditHito(hitoItem);
                        }}
                        style={{
                          background: "rgba(255, 255, 255, 0.05)",
                          border: "1px solid var(--line, rgba(255, 255, 255, 0.1))",
                          color: "#7d96ff",
                          borderRadius: "6px",
                          padding: "6px 9px",
                          fontSize: "11.5px",
                          fontWeight: 600,
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                        title="Editar hito"
                      >
                        <Pencil size={12} />
                        <span>Editar</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => onDeleteHito(hitoItem)}
                        style={{
                          background: "rgba(239, 68, 68, 0.08)",
                          border: "1px solid rgba(239, 68, 68, 0.2)",
                          color: "#ef4444",
                          borderRadius: "6px",
                          padding: "6px 9px",
                          fontSize: "11.5px",
                          fontWeight: 600,
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                        title="Eliminar hito"
                      >
                        <Trash2 size={12} />
                        <span>Eliminar</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
